import { Form, Input, InputPhone } from '../components';
import { Alert } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const PHONE_PATTERN = '^\\+(?:[0-9] ?){6,14}[0-9]$';
const EMAIL_PATTERN = '[A-Z0-9a-z._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,64}';
const LICENSE_PLATE_CHARS = /^[A-Z0-9ÜÆØÅÄÖ]*$/;

const FullWidthRow = styled.div`
  flex: 0 0 100%;
  width: 100%;
  box-sizing: border-box;
`;

const CustomerFormFields = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px 20px;
  width: 100%;
  align-items: flex-start;

  > .ant-form-item {
    flex: 1 1 220px;
    min-width: 0;
    max-width: 100%;
  }

  > ${FullWidthRow} {
    flex: 0 0 100%;
  }

  @media (max-width: 600px) {
    > .ant-form-item {
      flex: 1 1 270px;
      max-width: 450px;
    }
  }
`;

const StyledFormItem = styled(Form.Item)`
  min-width: 0;
`;

const LicensePlateInput = ({ value, onChange, ...rest }) => {
  const handleChange = e => {
    const next = e.target.value.toUpperCase();
    if (LICENSE_PLATE_CHARS.test(next)) {
      onChange(next);
    }
  };

  return (
    <Input
      className="input-licenseplate"
      {...rest}
      value={value}
      onChange={handleChange}
    />
  );
};

const CustomerForm = ({ kiosk, phoneCode, setPhoneCode, hidden }) => {
  const { t } = useTranslation();
  const [ emailError, setEmailError ] = useState(false);
  const [ phoneError, setPhoneError ] = useState(false);

  const validateEmail = (_, value) => {
    if (kiosk?.emailMandatory && !value) {
      setEmailError(false); // Don't show Alert for required validation
      return Promise.reject(new Error(t('error.fieldRequired', { entity: t('email') })));
    }
    if (value && !value.match(EMAIL_PATTERN)) {
      setEmailError(true); // Show Alert for format validation
      return Promise.reject(new Error(t('error.formatNotValid', { entity: t('email') })));
    }
    setEmailError(false);
    return Promise.resolve();
  };

  const validatePhoneNumber = async (_, value) => {
    setPhoneError(value && !(phoneCode + value).match(PHONE_PATTERN));
  };

  return (<CustomerFormFields>
    <StyledFormItem
      label={t('licensePlate')}
      name="licensePlate"
      rules={[ { required: !hidden } ]}
      help={<div/>}
      hidden={hidden}
    >
      <LicensePlateInput/>
    </StyledFormItem>

    {kiosk?.askContactPhone && <StyledFormItem
      label={t('phoneNumber') + ' (' + t('optional') + ')'}
      name="phoneNumber"
      rules={[ { validator: validatePhoneNumber } ]}
      validateTrigger="onBlur"
      hidden={hidden}
    >
      <InputPhone
        defaultCountry={kiosk?.countryCode}
        phoneCode={phoneCode}
        setPhoneCode={setPhoneCode}
        phoneCountrySelectAriaLabel={t('a11y.phoneCountryPrefix')}
      />
    </StyledFormItem>}

    {kiosk?.askContactEmail && <StyledFormItem
      label={t('email') + (kiosk?.emailMandatory ? ' (' + t('mandatory') + ')' : ' (' + t('optional') + ')')}
      name="email"
      rules={[
        { required: kiosk?.emailMandatory },
        { validator: validateEmail },
      ]}
      validateTrigger="onBlur"
      help={<div />}
      hidden={hidden}
    >
      <Input className="input-email" type="email"/>
    </StyledFormItem>}

    {phoneError && <FullWidthRow>
      <Alert
        type="error"
        message={t('error.formatNotValid', { entity: t('phoneNumber') })}
      />
    </FullWidthRow>}
    {emailError && <FullWidthRow>
      <Alert
        type="error"
        message={t('error.formatNotValid', { entity: t('email') })}
      />
    </FullWidthRow>}
  </CustomerFormFields>);
};

export default CustomerForm;
